/**
 * Dispositivos — tipos y etiquetas del inventario.
 *
 * Gemelo de `server/src/services/dispositivos/esquema.ts` y `tipos.ts`. Al
 * añadir un tipo de aparato, un estado o un motivo de retirada allí, hay que
 * añadirlo aquí o la pantalla enseñará el código crudo. No hay paquete
 * compartido entre cliente y servidor; es el mismo trato que `lib/personal.ts`.
 *
 * Los MODELOS, los TIPOS DE INCIDENCIA, los MÍNIMOS de stock y el KIT no
 * están aquí: son ajustes editables y se leen con `useCatalogosDispositivos`.
 */
export { fechaCorta, formatearTamano } from '@/lib/personal';

export type Marca = 'nuki' | 'switchbot' | 'ring' | 'otro';

export type TipoDispositivo =
  | 'smartlock'
  | 'opener'
  | 'bridge'
  | 'keypad'
  | 'sensor_puerta'
  | 'bot'
  | 'hub'
  | 'intercom'
  | 'cajetin'
  | 'mando_garaje'
  | 'enchufe';

export type Estado = 'stock' | 'instalado' | 'cuarentena' | 'reparacion' | 'baja' | 'perdido';
export type TipoUbicacion = 'oficina' | 'almacen' | 'con_empleado';
export type MotivoRetirada = 'cambio_casa' | 'fin_contrato' | 'averia' | 'robo_perdida' | 'otro';
export type Gravedad = 'leve' | 'bloquea_acceso';
export type TipoDocumento = 'factura' | 'foto' | 'otro';
export type RolSmartlock = 'smartlock' | 'smartlock2';

export const MARCA_LABEL: Record<Marca, string> = {
  nuki: 'Nuki',
  switchbot: 'SwitchBot',
  ring: 'Ring',
  otro: 'Otro',
};

export const TIPO_LABEL: Record<TipoDispositivo, string> = {
  smartlock: 'Smartlock',
  opener: 'Opener',
  bridge: 'Bridge',
  keypad: 'Keypad',
  sensor_puerta: 'Sensor de puerta',
  bot: 'Bot',
  hub: 'Hub',
  intercom: 'Ring Intercom',
  cajetin: 'Cajetín',
  mando_garaje: 'Mando de garaje',
  enchufe: 'Enchufe',
};

export const TIPO_MARCA: Record<TipoDispositivo, Marca> = {
  smartlock: 'nuki',
  opener: 'nuki',
  bridge: 'nuki',
  keypad: 'nuki',
  sensor_puerta: 'nuki',
  bot: 'switchbot',
  hub: 'switchbot',
  intercom: 'ring',
  cajetin: 'otro',
  mando_garaje: 'otro',
  enchufe: 'otro',
};

/** Los que se sincronizan con la API del fabricante. */
export const TIPO_TIENE_API: Record<TipoDispositivo, boolean> = {
  smartlock: true,
  opener: true,
  bridge: false,
  keypad: false,
  sensor_puerta: false,
  bot: true,
  hub: true,
  intercom: false,
  cajetin: false,
  mando_garaje: false,
  enchufe: false,
};

/** Custom field de Hostify que ocupa al instalarse; el segundo smartlock va al 1840. */
export const TIPO_CUSTOM_FIELD: Partial<Record<TipoDispositivo, number>> = {
  smartlock: 1236,
  opener: 2503,
  bot: 2504,
};

export function customFieldDe(tipo: TipoDispositivo, rol?: RolSmartlock): number | null {
  if (tipo === 'smartlock' && rol === 'smartlock2') return 1840;
  return TIPO_CUSTOM_FIELD[tipo] ?? null;
}

export const TIPOS: TipoDispositivo[] = Object.keys(TIPO_LABEL) as TipoDispositivo[];

/** Filtro «modelo»: el hueco, no un modelo. Espejo de `esquema.ts`. */
export const SIN_MODELO = 'sin';

export const ESTADO_LABEL: Record<Estado, string> = {
  stock: 'En stock',
  instalado: 'Instalado',
  cuarentena: 'En cuarentena',
  reparacion: 'En reparación',
  baja: 'De baja',
  perdido: 'Perdido',
};

export const ESTADO_STYLE: Record<Estado, string> = {
  stock: 'bg-sky-100 text-sky-700',
  instalado: 'bg-green-100 text-green-700',
  cuarentena: 'bg-amber-100 text-amber-700',
  reparacion: 'bg-orange-100 text-orange-700',
  baja: 'bg-gray-100 text-gray-500',
  perdido: 'bg-red-100 text-red-700',
};

/** En estos hay que decir dónde está físicamente. */
export const ESTADOS_CON_UBICACION: Estado[] = ['stock', 'cuarentena'];

export const UBICACION_LABEL: Record<TipoUbicacion, string> = {
  oficina: 'Oficina',
  almacen: 'Almacén / lavandería',
  con_empleado: 'Con un empleado / furgoneta',
};

export const MOTIVO_RETIRADA_LABEL: Record<MotivoRetirada, string> = {
  cambio_casa: 'Cambio de casa',
  fin_contrato: 'Fin de contrato',
  averia: 'Avería',
  robo_perdida: 'Robo o pérdida',
  otro: 'Otro',
};

export const GRAVEDAD_LABEL: Record<Gravedad, string> = {
  leve: 'Leve',
  bloquea_acceso: 'Bloquea el acceso',
};

export const TIPO_DOCUMENTO_LABEL: Record<TipoDocumento, string> = {
  factura: 'Factura',
  foto: 'Foto',
  otro: 'Otro',
};

// ─── Estructuras ──────────────────────────────────────────────────────────

export interface Ubicacion {
  tipo: TipoUbicacion;
  nombre?: string;
}

export interface Reparacion {
  proveedor: string;
  enviadoEl: string;
  referencia?: string;
}

export interface Instalacion {
  listingId: string;
  nickname: string;
  principal: boolean;
  desde: string;
  rol?: RolSmartlock;
  hostifyPendiente?: true;
}

export interface Compra {
  fecha: string | null;
  proveedor: string | null;
  precio: number | null;
  factura: string | null;
  garantiaHasta: string | null;
}

export interface Snapshot {
  en: string;
  online: boolean | null;
  bateria: number | null;
  bateriaCritica: boolean | null;
  bateriaKeypadCritica: boolean | null;
  bateriaSensorCritica: boolean | null;
  firmware: string | null;
  hardware: string | null;
  extra: Record<string, unknown>;
}

export interface Resumen {
  incidencias: number;
  ultimaIncidencia: { fecha: string; tipo: string } | null;
  incidencias90d: number;
  ultimoCambioPilas: string | null;
  /** Cuántas veces se ha instalado. 0 = sin estrenar. */
  instalacionesTotales: number;
  ultimoEventoNuki: string | null;
}

/**
 * Sin estrenar: nunca ha estado instalado. No es un estado que nadie marque,
 * sale de contar las instalaciones de su historial.
 */
export function sinEstrenar(d: { resumen: Resumen }): boolean {
  return d.resumen.instalacionesTotales === 0;
}

export interface DocumentoDispositivo {
  id: string;
  fileName: string;
  blobPath: string;
  contentType: string;
  size: number;
  uploadedAt: string;
  uploadedBy?: string;
  tipo: TipoDocumento;
}

export interface Dispositivo {
  id: string;
  type: 'dispositivo';
  marca: Marca;
  tipo: TipoDispositivo;
  modelo: string | null;
  idFabricante: string;
  numeroSerie: string | null;
  alias: string | null;
  tieneApi: boolean;
  estado: Estado;
  ubicacion: Ubicacion | null;
  reparacion: Reparacion | null;
  estadoDesde: string;
  instalaciones: Instalacion[];
  enlazadoA: string | null;
  hubDeviceId: string | null;
  compra: Compra;
  snapshot: Snapshot | null;
  resumen: Resumen;
  documentos: DocumentoDispositivo[];
  notas: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
  sincronizadoEn: string | null;
}

/** Fila de la lista: sin documentos ni notas. */
export type FilaDispositivo = Omit<Dispositivo, 'documentos' | 'notas'> & { numDocumentos: number; tieneNotas: boolean };

export interface ResultadoHostify {
  escrito: boolean;
  campo?: number;
  error?: string;
}

export type EntradaHistorial = {
  id: string;
  dispositivoId: string;
  fecha: string;
  registradoEn: string;
  por: string | null;
} & (
  | { tipo: 'alta'; origen: 'manual' | 'importacion'; nota?: string }
  | { tipo: 'instalacion'; listingId: string; nickname: string; principal: boolean; rol?: RolSmartlock; hostify: ResultadoHostify | null; nota?: string }
  | { tipo: 'retirada'; listingId: string; nickname: string; motivo: MotivoRetirada; hostify: ResultadoHostify | null; nota?: string }
  | { tipo: 'estado'; de: Estado; a: Estado; ubicacion?: Ubicacion; reparacion?: Reparacion; nota?: string }
  | { tipo: 'pilas'; nota?: string }
  | {
      tipo: 'incidencia';
      incidenciaTipo: string;
      gravedad: Gravedad;
      descripcion: string;
      queSeHizo?: string;
      origen: 'manual' | 'auto';
      fuente?: string;
      ocurrencias?: number;
    }
  | { tipo: 'snapshot_cambio'; que: 'offline' | 'online' | 'bateria_critica' | 'bateria_ok' | 'firmware'; de?: string; a?: string }
  | { tipo: 'enlace'; enlazadoA: string | null; antes: string | null }
  | { tipo: 'nota'; texto: string }
  | { tipo: 'documento'; accion: 'subido' | 'borrado'; fileName: string }
);

export type TipoEntrada = EntradaHistorial['tipo'];

export const ENTRADA_LABEL: Record<TipoEntrada, string> = {
  alta: 'Alta',
  instalacion: 'Instalación',
  retirada: 'Retirada',
  estado: 'Cambio de estado',
  pilas: 'Pilas cambiadas',
  incidencia: 'Incidencia',
  snapshot_cambio: 'Cambio detectado',
  enlace: 'Enlace',
  nota: 'Nota',
  documento: 'Documento',
};

/** Las que una persona puede borrar; el resto se corrige con el movimiento contrario. */
export const ENTRADAS_BORRABLES: TipoEntrada[] = ['incidencia', 'pilas', 'nota'];

export interface Tramo {
  desde: string;
  hasta: string | null;
  motivo?: string;
}

export interface CasaDondeHaEstado {
  listingId: string;
  nickname: string;
  tramos: Tramo[];
}

export interface ResumenEnlace {
  id: string;
  tipo: TipoDispositivo;
  idFabricante: string;
  alias: string | null;
  estado: Estado;
  hubDeviceId: string | null;
}

export interface FichaDispositivo {
  dispositivo: Dispositivo;
  historial: EntradaHistorial[];
  casas: CasaDondeHaEstado[];
  enlazados: ResumenEnlace[];
  enlazadoA: ResumenEnlace | null;
  transiciones: Estado[];
}

/** Lo que devuelve cualquier acción sobre la ficha. */
export interface AccionDispositivo {
  dispositivo: Dispositivo;
  historial: EntradaHistorial[];
  casas?: CasaDondeHaEstado[];
  transiciones?: Estado[];
  cambios?: unknown[];
  fallos?: string[];
}

export interface ListadoDispositivos {
  dispositivos: FilaDispositivo[];
  total: number;
  instalados: number;
  enStock: number;
  cuarentena: number;
  /** Cuántos no tienen modelo, contados antes de filtrar por modelo. */
  sinModelo: number;
  /** Los modelos que hay en la lista, para el desplegable. */
  modelosEnLista: { id: string; n: number }[];
}

export interface OpcionPropiedad {
  id: string;
  nickname: string;
}

export interface ModeloAjuste {
  id: string;
  label: string;
  marca: Marca;
  tipo: TipoDispositivo;
  wifiIntegrado: boolean;
}

export interface IncidenciaTipoAjuste {
  id: string;
  label: string;
}

export interface MinimoAjuste {
  id: TipoDispositivo;
  label: string;
  minimo: number;
}

export interface KitAjuste {
  id: TipoDispositivo;
  label: string;
  cantidad: number;
}

export interface FilaStockTipo {
  tipo: TipoDispositivo;
  etiqueta: string;
  sinEstrenar: number;
  usados: number;
  cuarentena: number;
  reparacion: number;
  disponibles: number;
  instalados: number;
  minimo: number;
  bajoMinimo: boolean;
  kit: number;
}

export interface FaltaKit {
  tipo: TipoDispositivo;
  etiqueta: string;
  necesarios: number;
  disponibles: number;
}

export interface UnidadEnStock {
  id: string;
  tipo: TipoDispositivo;
  sinEstrenar: boolean;
  modelo: string | null;
  idFabricante: string;
  alias: string | null;
  estado: Estado;
  ubicacion: Ubicacion | null;
  reparacion: Reparacion | null;
  estadoDesde: string;
  incidencias: number;
  incidencias90d: number;
  ultimaIncidencia: { fecha: string; tipo: string } | null;
}

export interface ResumenStock {
  porTipo: FilaStockTipo[];
  tiposBajoMinimo: number;
  kit: { casasCompletas: number; cuelloDeBotella: TipoDispositivo | null; faltaParaUna: FaltaKit[] };
  unidades: UnidadEnStock[];
}

export interface ContadorAtencion {
  bajoMinimo: number;
  hostifyPendiente: number;
  discrepancias: number;
  desglose: {
    bajoMinimo: number;
    hostifyPendiente: number;
    hostifyDistinto: number;
    enStockHabla: number;
    faltasCobertura: number;
    casasIncompletas: number;
    bridgesSobrantes: number;
  };
  atencion: number;
}

/**
 * El número del menú, partido y en palabras, para enseñarlo al pasar el
 * ratón. Solo lo que no es cero, y en el orden en que se arregla: primero lo
 * que se compra (stock), luego lo que se escribe (Hostify), y al final lo que
 * hay que ir a mirar a Auditoría.
 */
export function desgloseAtencion(c: ContadorAtencion | null | undefined): string[] {
  if (!c?.desglose) return [];
  const d = c.desglose;
  const s = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;
  const partes: [number, string][] = [
    [d.bajoMinimo, s(d.bajoMinimo, 'tipo bajo mínimo de stock', 'tipos bajo mínimo de stock')],
    [d.hostifyPendiente, s(d.hostifyPendiente, 'pendiente de escribir en Hostify', 'pendientes de escribir en Hostify')],
    [d.hostifyDistinto, s(d.hostifyDistinto, 'casa donde Hostify y el inventario no coinciden', 'casas donde Hostify y el inventario no coinciden')],
    [d.enStockHabla, s(d.enStockHabla, 'en stock que da señales de vida', 'en stock que dan señales de vida')],
    [d.faltasCobertura, s(d.faltasCobertura, 'falta de cobertura (bridge, hub o smartlock)', 'faltas de cobertura (bridge, hub o smartlock)')],
    [d.casasIncompletas, s(d.casasIncompletas, 'casa sin smartlock o sin keypad', 'casas sin smartlock o sin keypad')],
    [d.bridgesSobrantes, s(d.bridgesSobrantes, 'bridge que sobra', 'bridges que sobran')],
  ];
  return partes.filter(([n]) => n > 0).map(([, texto]) => texto);
}

// ─── Incidencias globales ─────────────────────────────────────────────────

export interface FilaIncidencia {
  id: string;
  fecha: string;
  dispositivoId: string;
  idFabricante: string;
  alias: string | null;
  tipoDispositivo: TipoDispositivo;
  modelo: string | null;
  casa: string | null;
  incidenciaTipo: string;
  gravedad: Gravedad;
  origen: 'manual' | 'auto';
  fuente?: string;
  ocurrencias?: number;
  descripcion: string;
  queSeHizo?: string;
  por: string | null;
}

export interface Reincidente {
  dispositivoId: string;
  idFabricante: string;
  alias: string | null;
  tipoDispositivo: TipoDispositivo;
  modelo: string | null;
  casa: string | null;
  incidencias: number;
  ultima: string;
  tipos: string[];
}

export interface FilaPorModelo {
  modelo: string | null;
  etiqueta: string;
  tipoDispositivo: TipoDispositivo | null;
  unidades: number;
  incidencias: number;
  porUnidadMes: number;
}

export interface FilaPorLote {
  lote: string;
  unidades: number;
  incidencias: number;
  porUnidadMes: number;
}

export interface ResumenIncidencias {
  desde: string;
  hasta: string;
  total: number;
  manuales: number;
  automaticas: number;
  bloquean: number;
  filas: FilaIncidencia[];
  reincidentes: Reincidente[];
  porModelo: FilaPorModelo[];
  porLote: FilaPorLote[];
}

// ─── Auditoría ────────────────────────────────────────────────────────────

export interface ResumenDispositivo {
  id: string;
  tipo: TipoDispositivo;
  idFabricante: string;
  alias: string | null;
  estado: Estado;
}

export type CampoHostify = 1236 | 1840 | 2503 | 2504;

export interface Discrepancia {
  listingId: string;
  nickname: string;
  campo: CampoHostify;
  hostify: string | null;
  inventario: ResumenDispositivo | null;
  fichaDeHostify: ResumenDispositivo | null;
  caso: 'hostify_sin_inventario' | 'inventario_sin_hostify' | 'distintos' | 'pendiente';
}

export interface AuditoriaDispositivos {
  hoy: string;
  local: {
    hostifyVsInventario: Discrepancia[];
    enStockPeroHabla: { dispositivo: ResumenDispositivo; senal: 'online' | 'eventos'; cuando: string }[];
    cobertura: {
      faltas: { dispositivo: ResumenDispositivo; listingId: string; nickname: string; necesita: 'bridge' | 'hub' | 'smartlock' | 'opener'; detalle?: string }[];
      casasIncompletas: { listingId: string; nickname: string; falta: TipoDispositivo[] }[];
      /** Bridges instalados donde ya nada los necesita. No se retiran solos. */
      bridgesSobrantes?: { dispositivo: ResumenDispositivo; listingId: string; nickname: string }[];
    };
    incompletos: { dispositivo: ResumenDispositivo; falta: ('modelo' | 'serie')[] }[];
    discrepancias: number;
  };
  cuentas: {
    enCuentaSinFicha: { marca: 'nuki' | 'switchbot'; tipo: TipoDispositivo; idFabricante: string; alias: string | null }[];
    customFieldsRotos: { listingId: string; nickname: string; campo: CampoHostify; valor: string }[];
  } | null;
  errorCuentas: string | null;
}

export const CASO_LABEL: Record<Discrepancia['caso'], string> = {
  hostify_sin_inventario: 'Hostify lo tiene; el inventario, no',
  inventario_sin_hostify: 'El inventario lo tiene; Hostify, no',
  distintos: 'Dicen cosas distintas',
  pendiente: 'Pendiente de escribir en Hostify',
};

/**
 * Lo que devuelve «poner al día»: el informe de la migración de datos ya
 * guardados cuando cambia un criterio (nombres, estados, modelos y los
 * accesorios que la cuenta ya no confirma). En simulacro salvo `aplicar`.
 */
export interface InformeMigracion {
  aplicado: boolean;
  avisoCuenta: string | null;
  actualizar: { id: string; que: string[] }[];
  borrar: { id: string; motivo: string }[];
  revisar: string[];
  sinCambios: number;
}

export interface ResultadoSincronizacion {
  contadores: {
    creados: number;
    actualizados: number;
    instalacionesDeducidas: number;
    enStock: number;
    sinModelo: number;
    customFieldsRotos: number;
    yaNoEnCuenta: number;
    listingsActivos: number;
    listingsConOpener: number;
    /** Bridges dados de alta solos porque la casa lo necesita. */
    bridgesDeducidos?: number;
  };
  avisos: string[];
  /** Lo que la sincronización ha quitado o puesto al día por su cuenta. */
  limpieza: { accesoriosBorrados: number; fichasActualizadas: number };
}

// ─── Utilidades de presentación ───────────────────────────────────────────

export function textoUbicacion(u: Ubicacion | null | undefined): string {
  if (!u) return '—';
  return u.nombre ? `${UBICACION_LABEL[u.tipo]} · ${u.nombre}` : UBICACION_LABEL[u.tipo];
}

/** Dónde está: las casas (la principal con asterisco), o la ubicación de stock, o el taller. */
export function textoDonde(d: Pick<Dispositivo, 'estado' | 'instalaciones' | 'ubicacion' | 'reparacion'>): string {
  if (d.estado === 'instalado') {
    return d.instalaciones.map((i) => (i.principal && d.instalaciones.length > 1 ? `${i.nickname}*` : i.nickname)).join(', ') || '—';
  }
  if (d.estado === 'reparacion') return d.reparacion ? `En ${d.reparacion.proveedor} desde ${fechaCortaLocal(d.reparacion.enviadoEl)}` : 'En reparación';
  if (d.estado === 'perdido' || d.estado === 'baja') return '—';
  return textoUbicacion(d.ubicacion);
}

function fechaCortaLocal(iso?: string | null): string {
  if (!iso) return '—';
  const [anio, mes, dia] = iso.slice(0, 10).split('-');
  const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const m = MESES[Number(mes) - 1];
  return m ? `${Number(dia)} ${m} ${anio}` : iso;
}

export function fechaHora(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-ES', { timeZone: 'Europe/Madrid', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function diasDesde(iso?: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
}

/** Garantía: en vigor, vencida o sin fecha de compra. */
export function estadoGarantia(compra: Compra): { texto: string; vigente: boolean | null } {
  if (!compra.garantiaHasta) return { texto: compra.fecha ? 'Sin garantía' : 'Sin fecha de compra', vigente: null };
  const hoy = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
  const vigente = compra.garantiaHasta >= hoy;
  return { texto: `${vigente ? 'Hasta' : 'Venció el'} ${fechaCortaLocal(compra.garantiaHasta)}`, vigente };
}

/** Qué se ve en la batería: crítica manda sobre el porcentaje. */
export function textoBateria(s: Snapshot | null): { texto: string; alerta: boolean } | null {
  if (!s) return null;
  const critica = s.bateriaCritica || s.bateriaKeypadCritica || s.bateriaSensorCritica;
  if (critica) {
    const partes: string[] = [];
    if (s.bateriaCritica) partes.push('pilas');
    if (s.bateriaKeypadCritica) partes.push('keypad');
    if (s.bateriaSensorCritica) partes.push('sensor');
    return { texto: `Crítica (${partes.join(', ')})`, alerta: true };
  }
  if (s.bateria !== null) return { texto: `${s.bateria} %`, alerta: false };
  if (s.bateriaCritica === false) return { texto: 'Bien', alerta: false };
  return null;
}
